class SliderReviewsComponent extends HTMLElement {
  constructor() {
    super();

    this.mq = window.matchMedia('(min-width: 990px)');

    this.slider = this.querySelector('.slider-reviews-component__slides');
    this.items = Array.from(this.slider?.children || []);

    if (!this.slider || this.items.length === 0) return;

    this.prevButton = this.querySelector('.slider-reviews-component__button--prev');
    this.nextButton = this.querySelector('.slider-reviews-component__button--next');
    this.progressBar = this.querySelector('.slider-reviews-component__progress-bar');

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    this._scrollDebounce = null;

    this.readMoreButton = this.querySelector('.slider-reviews__read-more');

    this.onDragStart = this.onDragStart.bind(this);
    this.onDragMove  = this.onDragMove.bind(this);
    this.onDragEnd   = this.onDragEnd.bind(this);

    this.setupMediaListener();
    this.initIfDesktop();

    if(this.readMoreButton) this.readMoreButton.addEventListener('click', () => this.handleReadMoreClick());
  }

  handleReadMoreClick() {
    const expanded = this.readMoreButton.getAttribute('aria-expanded') === 'true';
    this.readMoreButton.textContent = expanded ? 'READ MORE REVIEWS' : 'READ LESS';
    this.readMoreButton.setAttribute('aria-expanded', String(!expanded));
  }

  setupMediaListener() {
    const handler = () => this.initIfDesktop();
    if (this.mq.addEventListener) this.mq.addEventListener('change', handler);
    else this.mq.addListener(handler);
  }

  initIfDesktop() {

    const styles = getComputedStyle(this.slider);
    const gapStr = styles.columnGap || styles.gap || '0';
    this.gap = parseFloat(gapStr) || 0;

    this.itemWidth = this.items[0].getBoundingClientRect().width;
    this.step = this.itemWidth + this.gap;

    this.teardown();
    this.prevButton?.addEventListener('click', this.onButtonClick);
    this.nextButton?.addEventListener('click', this.onButtonClick);
    this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    this.slider.addEventListener('mousedown', this.onDragStart);
    window.addEventListener('resize', this.onResize);

    this.updateUI();
  }

  teardown() {
    this.prevButton?.removeEventListener('click', this.onButtonClick);
    this.nextButton?.removeEventListener('click', this.onButtonClick);
    this.slider?.removeEventListener('scroll', this.onScroll);
    this.slider?.removeEventListener('mousedown', this.onDragStart);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mousemove', this.onDragMove);
    window.removeEventListener('mouseup', this.onDragEnd);
  }

  onResize() {
    if (!this.mq.matches) {
      this.teardown();
      return;
    }
    const styles = getComputedStyle(this.slider);
    const gapStr = styles.columnGap || styles.gap || '0';
    this.gap = parseFloat(gapStr) || 0;

    this.itemWidth = this.items[0].getBoundingClientRect().width;
    this.step = this.itemWidth + this.gap;

    this.updateUI();
  }

  onButtonClick(e) {
    if (!this.mq.matches) return;

    const isNext = e.currentTarget.classList.contains('slider-reviews-component__button--next');
    const dir = isNext ? 1 : -1;

    const maxLeft = this.slider.scrollWidth - this.slider.clientWidth;
    const target = this.clamp(this.slider.scrollLeft + dir * this.step, 0, maxLeft);

    this.slider.scrollTo({ left: target, behavior: 'smooth' });
  }

  onScroll() {
    this.updateUI();

    this.classList.add('is-scrolling');
    clearTimeout(this._scrollDebounce);
    this._scrollDebounce = setTimeout(() => {
      this.classList.remove('is-scrolling');
    }, 120);
  }

  updateUI(optionalLeft) {
    const left = typeof optionalLeft === 'number' ? optionalLeft : this.slider.scrollLeft;
    const max = Math.max(this.slider.scrollWidth - this.slider.clientWidth, 1);

    const pct = Math.min(Math.max(left / max, 0), 1) * 100;
    if (this.progressBar) this.progressBar.style.setProperty('--after-width', pct + '%');

    const atStart = left <= 0;
    const atEnd = left >= max - 1;
    this.prevButton?.toggleAttribute('disabled', atStart);
    this.nextButton?.toggleAttribute('disabled', atEnd);
  }

  clamp(v, min, max) {
    return Math.min(Math.max(v, min), max);
  }

  onDragStart(e) {
    if (!this.mq.matches) return;
    if (e.button !== 0) return;

    this.isDragging = true;
    this.startX = e.clientX;
    this.startScrollLeft = this.slider.scrollLeft;

    this._prevScrollBehavior = this.slider.style.scrollBehavior;
    this.slider.style.scrollBehavior = 'auto';

    this.slider.classList.add('is-dragging');
    this.classList.add('is-scrolling');

    window.addEventListener('mousemove', this.onDragMove, { passive: false });
    window.addEventListener('mouseup', this.onDragEnd);
  }

  onDragMove(e) {
    if (!this.isDragging) return;
    e.preventDefault();

    const dx = e.clientX - this.startX;
    const target = this.startScrollLeft - dx;
    const maxLeft = this.slider.scrollWidth - this.slider.clientWidth;
    this.slider.scrollLeft = this.clamp(target, 0, maxLeft);
  }

  onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;

    this.slider.classList.remove('is-dragging');

    this.slider.style.scrollBehavior = this._prevScrollBehavior || '';

    clearTimeout(this._scrollDebounce);
    this._scrollDebounce = setTimeout(() => {
      this.classList.remove('is-scrolling');
    }, 120);

    window.removeEventListener('mousemove', this.onDragMove);
    window.removeEventListener('mouseup', this.onDragEnd);
  }
}

customElements.define('slider-reviews-component', SliderReviewsComponent);
